import axios, { type AxiosProgressEvent } from 'axios';

import { EXCEL_CONTENT_TYPE_HEADER } from '@apple/utils/files/models/excel';
import { encodeProductPathSegment } from '@apple/utils/url';

import type {
	VerifyStoresResponse,
	Vintage,
	VintageBulkUploadOperation,
	VintageExcelResult,
	VintageListing,
	VintageProductRow,
	VintageSaveModel,
	VintageStoreRow,
} from './models';
import {
	verifyStoresResponseSchema,
	vintageExcelResultSchema,
	vintageListingSchema,
	vintageProductRowSchema,
	vintageSchema,
	vintageStoreRowSchema,
} from './models';

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController */
export async function getVintagesListing(): Promise<VintageListing[]> {
	const response = await axios.get('/api/management/vintages/listing');

	return vintageListingSchema.array().parse(response.data);
}

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController */
export async function getVintage(vintageId: number): Promise<Vintage> {
	const response = await axios.get(`/api/management/vintages/${vintageId}`);

	return vintageSchema.parse(response.data);
}

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController */
export async function getVintageStores(vintageId: number): Promise<VintageStoreRow[]> {
	const response = await axios.get(`/api/management/vintages/${vintageId}/stores`);

	return vintageStoreRowSchema.array().parse(response.data);
}

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController */
export async function getVintageProducts(vintageId: number): Promise<VintageProductRow[]> {
	const response = await axios.get(`/api/management/vintages/${vintageId}/products`);

	return vintageProductRowSchema.array().parse(response.data);
}

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController */
export async function saveProductVintages(itemId: string, vintageIds: number[]): Promise<void> {
	await axios.put(
		`/api/management/vintages/products/${encodeProductPathSegment(itemId)}`,
		vintageIds,
	);
}

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController */
export async function saveVintage(model: VintageSaveModel): Promise<Vintage> {
	const response = await axios.post('/api/management/vintages', model);

	return vintageSchema.parse(response.data);
}

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController */
export async function deleteVintage(vintageId: number) {
	await axios.delete(`/api/management/vintages/${vintageId}`);
}

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController */
export async function verifyStoreIds(storeIds: string[]): Promise<VerifyStoresResponse> {
	const response = await axios.post('/api/management/vintages/stores/verify-ids', storeIds);

	return verifyStoresResponseSchema.parse(response.data);
}

/**
 * Uploads a product bulk management Excel file.
 * @see AppleRetail.Web.Features.Vintage.VintageExcelController
 */
export async function uploadVintageAssociations(
	file: File,
	onUploadProgress?: (event: AxiosProgressEvent) => void,
): Promise<VintageExcelResult> {
	const response = await axios.post<unknown>('/api/management/vintages/bulk/excel', file, {
		headers: {
			...EXCEL_CONTENT_TYPE_HEADER,
		},
		onUploadProgress,
	});

	return vintageExcelResultSchema.parse(response.data);
}

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController */
export async function saveAssociations(associations: VintageBulkUploadOperation[]): Promise<void> {
	if (associations.length === 0) {
		return;
	}

	await axios.post('/api/management/vintages/associations', associations);
}

/** @see AppleRetail.Web.Features.Vintage.VintageManagementController */
export async function deleteAssociations(itemIds: string[]): Promise<void> {
	if (itemIds.length === 0) {
		return;
	}

	await axios.post('/api/management/vintages/associations/delete-multiple', itemIds);
}
